import { FC } from 'react';
import { useSelector } from 'react-redux';
import { graphql, useFragment } from 'react-relay';
import { useAuctionBids } from 'dibs-auctions/exports/hooks';

// components
import { PriceDisplay } from 'dibs-buyer-price';
import { FormattedMessage } from 'dibs-react-intl';
import { PdpSharedAuctionActionBidLabel } from './PdpSharedAuctionActionBidLabel';
import { PdpSharedAuctionBidButton } from '../PdpSharedAuctionBidButton/PdpSharedAuctionBidButton';
import { PdpSharedAuctionBidsDetails } from '../PdpSharedAuctionBidsDetails/PdpSharedAuctionBidsDetails';
import { OriginalListPrice } from 'dibs-auctions/exports/OriginalListPrice';

// types
import { PdpSharedAuctionActionBid_item$key as Item } from './__generated__/PdpSharedAuctionActionBid_item.graphql';
import { OnAuctionBidClick } from '../PdpSharedAuctionInfo/PdpSharedAuctionInfo';

// styles
import styles from './PdpSharedAuctionActionBid.scss';

type Props = {
    item: Item;
    currency: string;
    isBiddingDisabled?: boolean;
    onAuctionBidClick: OnAuctionBidClick;
    isCloseToReserve: boolean;
};

type RelayVariables = {
    userId: string;
};

type ReduxStoreType = {
    relayVariables: {
        variables: RelayVariables;
    };
};

const increaseBidLabel = (
    <FormattedMessage id="PdpSharedAuctionActionBid.increaseBid" defaultMessage="Increase Bid" />
);

const itemFragment = graphql`
    fragment PdpSharedAuctionActionBid_item on Item {
        ...PriceDisplay_item
            @arguments(
                userId: $userId
                isTrade: $isTrade
                userCountryCode: $userCountryCode
                priceBookName: $priceBookName
            )
        ...useAuctionBids_item @arguments(fetchUser: $fetchUser)
        ...PdpSharedAuctionBidsDetails_item
        ...PdpSharedAuctionActionBidLabel_item
        ...OriginalListPrice_item
    }
`;

export const PdpSharedAuctionActionBid: FC<Props> = ({
    item: itemRef,
    currency,
    onAuctionBidClick,
    isBiddingDisabled,
    isCloseToReserve,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const { userId } = useSelector<ReduxStoreType, RelayVariables>(
        relay => relay?.relayVariables?.variables || {}
    );
    const { hasBid } = useAuctionBids({ item, userId, currency });

    return (
        <>
            <OriginalListPrice
                item={item}
                currency={currency}
                className={styles.originalListPrice}
            />
            <div className={styles.container}>
                <span className={styles.bid}>
                    <FormattedMessage
                        id="PdpSharedAuctionActionBid.currentBid"
                        defaultMessage="Current Bid"
                    />
                </span>
                <div className={styles.priceInfo}>
                    <PriceDisplay currency={currency} item={item} useSvgIcon sku={null} />
                    <PdpSharedAuctionBidsDetails
                        item={item}
                        currency={currency}
                        className={styles.bidDetails}
                    />
                </div>
                <PdpSharedAuctionActionBidLabel
                    item={item}
                    currency={currency}
                    isCloseToReserve={isCloseToReserve}
                />
                <div className={styles.printHide}>
                    <PdpSharedAuctionBidButton
                        onClick={() => onAuctionBidClick(hasBid)}
                        disabled={isBiddingDisabled}
                        label={hasBid ? increaseBidLabel : null}
                    />
                </div>
            </div>
        </>
    );
};
